import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import {
  ChatTemplate,
  ChatTemplateHeader,
  ChatTemplateHeaderGroup,
  ChatTemplateOverview,
  ChatTemplateOverviewTitle,
} from "@arcadeai/design-system/components/ui/templates/chat-template";
import { ChatMessages } from "@arcadeai/ui-kit/chat/components/chat-messages";
import { ChatPrompt } from "@arcadeai/ui-kit/chat/components/chat-prompt";
import { ChatToolkitPickerAction } from "@arcadeai/ui-kit/chat/components/chat-toolkit-picker-action";
import { useChatRuntime } from "@arcadeai/ui-kit/chat/context";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { sessionQueryOptions } from "@/lib/auth-client";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { ChatHistoryTrigger, PlaygroundChatSidebar } from "./chat-sidebar";

export function PlaygroundChatLayout() {
  const chat = useChatRuntime();
  const router = useRouter();
  const { organizationId, projectId } = useOrgProjectRouteContext();
  const { data: session } = useSuspenseQuery(sessionQueryOptions);

  const userName = session?.user?.name ?? "there";
  const viewerId = session?.user?.id ?? "";

  function navigateToChatIndex() {
    router.navigate({
      to: "/orgs/$organizationId/projects/$projectId/playground/chat",
      params: { organizationId, projectId },
    });
  }

  function handleNewChat() {
    chat.newChat();
    navigateToChatIndex();
  }

  async function handleDeleteChat(id: string) {
    const wasActive = await chat.deleteChat(id);
    if (wasActive) {
      navigateToChatIndex();
    }
  }

  async function handleDeleteAll() {
    const deleted = await chat.deleteAll();
    if (deleted) {
      navigateToChatIndex();
    }
  }

  return (
    <PlaygroundChatSidebar
      onDeleteAll={handleDeleteAll}
      onDeleteChat={handleDeleteChat}
      onNewChat={handleNewChat}
      viewerId={viewerId}
    >
      <ChatTemplate className="h-full min-h-0">
        <ChatTemplateHeader>
          <ChatTemplateHeaderGroup className="gap-2">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    aria-label="New chat"
                    className="h-9 gap-2 px-2 text-foreground"
                    onClick={handleNewChat}
                    variant="outline"
                  />
                }
              >
                <PlusIcon className="size-4" />
                <span className="sr-only md:not-sr-only">New Chat</span>
              </TooltipTrigger>
              <TooltipContent>New chat</TooltipContent>
            </Tooltip>
            <ChatToolkitPickerAction />
          </ChatTemplateHeaderGroup>

          <div className="flex-1" />

          <ChatTemplateHeaderGroup className="gap-2">
            <ChatHistoryTrigger />
          </ChatTemplateHeaderGroup>
        </ChatTemplateHeader>

        <ChatMessages
          className="min-h-0"
          emptyState={
            <div className="flex min-h-0 min-w-0 flex-1 items-start justify-center overflow-hidden">
              <ChatTemplateOverview className="max-w-2xl [@media(max-height:820px)]:hidden">
                <ChatTemplateOverviewTitle>
                  Hi, <span className="text-brand-text/90">{userName}</span>
                </ChatTemplateOverviewTitle>
                <p className="text-balance text-[clamp(1.125rem,2vw,1.5rem)] text-muted-foreground leading-relaxed">
                  Explore how to call tools in your agent with Arcade.
                </p>
              </ChatTemplateOverview>
            </div>
          }
        />

        <ChatPrompt autoFocus showToolkitPicker={false} />
      </ChatTemplate>
    </PlaygroundChatSidebar>
  );
}
